import type { StyleSheet } from "../types/style.js";
import { StyleParser } from "../parser/style-parser.js";
import type { DiagramTheme } from "../renderer/palette.js";
import { REFERENCE_DATA } from "./reference-data.js";

/**
 * Translated labels for the built-in annotation badges, injected by the
 * caller per docs/spec/i18n.md (same pattern as EmptyStateLabels). Omitted
 * keys fall back to the `reference-data.ts` en labels, so the builtin
 * sheet and the Reference panel can never drift (TPL-1415).
 */
export interface AnnotationBadgeLabels {
  deprecated?: string;
  new?: string;
  experimental?: string;
  draft?: string;
  planned?: string;
  migrationTarget?: string;
}

/** reference-data annotation name → AnnotationBadgeLabels key. */
export const ANNOTATION_LABEL_KEYS: Record<string, keyof AnnotationBadgeLabels> = {
  deprecated: "deprecated",
  new: "new",
  experimental: "experimental",
  draft: "draft",
  planned: "planned",
  migration_target: "migrationTarget",
};

/** Escape a label for embedding in a double-quoted style string literal. */
function escapeStyleString(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Generate the `@deprecated` / `@new` / `@experimental` / `@draft` /
 * `@planned` / `@migration_target` rule blocks from `reference-data.ts` (colors /
 * icons / en label defaults) plus optional injected labels.
 */
function buildAnnotationRules(theme: DiagramTheme, badgeLabels?: AnnotationBadgeLabels): string {
  return REFERENCE_DATA.annotations
    .map((a) => {
      const label = badgeLabels?.[ANNOTATION_LABEL_KEYS[a.name]] ?? a.defaultBadge.label.en;
      // Both palettes come from `reference-data.ts`, so the Reference panel's
      // swatch and this sheet cannot disagree about what the theme paints
      // (#2482; same single-source reasoning as the labels above, TPL-1415).
      const color = a.defaultBadge.color[theme];
      const extra = a.name === "deprecated" ? "\n  opacity: 0.6;" : "";
      return `@${a.name} {
  badge-color: ${color};
  badge-icon: "${a.defaultBadge.icon}";
  badge-label: "${escapeStyleString(label)}";${extra}
}`;
    })
    .join("\n\n");
}

const ANNOTATION_RULES_PLACEHOLDER = "/* __ANNOTATION_RULES__ */";

/**
 * karasu built-in default theme (dark) — template whose annotation section
 * is a placeholder filled by {@link buildBuiltinStyleSource}.
 * This is the single source of truth for all default styling.
 * Applied as the lowest-priority cascade layer — user stylesheets override these.
 */
const BUILTIN_STYLE_TEMPLATE: string = `/* karasu built-in default theme */

/* ── ノード種別 ── */
user {
  background-color: #1D4ED8;
  color: #FFFFFF;
  border-color: #1E40AF;
  border-width: 2;
  shape: user;
  font-weight: bold;
  font-size: 13;
}

service {
  background-color: #0369A1;
  color: #FFFFFF;
  border-color: #075985;
  border-width: 2;
  shape: box;
  font-weight: bold;
  font-size: 13;
}

client {
  background-color: #6D28D9;
  color: #FFFFFF;
  border-color: #5B21B6;
  border-width: 2;
  shape: box;
  font-weight: bold;
  font-size: 13;
}

domain {
  background-color: #1E3A5F;
  color: #E0F2FE;
  border-color: #0C4A6E;
  border-width: 1;
  shape: box;
  font-size: 12;
}

/* usecase is fill-less: behaviour reads lighter than the structure that holds
   it, and the card's border becomes its only outline — so that border carries
   the 3:1 non-text bar over three surfaces at once: the bare canvas, up to
   three stacked boundary tints, and the card at the opacity @deprecated fades
   it to. That last one is what pushes this border so far from the canvas; a
   purely visual pick would sit much closer (docs/spec/style.md § Kind color
   vocabulary). */
usecase {
  background-color: transparent;
  color: #BAE0F7;
  border-color: #A8C8E0;
  border-width: 1;
  shape: box;
  font-size: 12;
}

entity {
  background-color: #3B2F63;
  color: #EDE9FE;
  border-color: #7C3AED;
  border-width: 1;
  shape: box;
  font-size: 12;
}

/* resource is a logical *reference* to something the physical layer owns, not a
   thing this layer holds — neutral slate says "not mine" where the navy of
   domain / member says "mine". */
resource {
  background-color: #26303F;
  color: #D7DEE8;
  border-color: #64748B;
  border-width: 2;
  font-size: 12;
}

team {
  background-color: #065F46;
  color: #D1FAE5;
  border-color: #047857;
  border-width: 2;
  shape: box;
  font-weight: bold;
  font-size: 13;
}

member {
  background-color: #1E3A5F;
  color: #E0F2FE;
  border-color: #0C4A6E;
  border-width: 1;
  shape: user;
  font-size: 12;
}

/* ── インフラリソース種別 ── */
database {
  background-color: #1C3A2E;
  color: #86EFAC;
  border-color: #166534;
  border-width: 2;
  shape: cylinder;
  font-weight: bold;
  font-size: 13;
}

queue {
  background-color: #2D2310;
  color: #FCD34D;
  border-color: #92400E;
  border-width: 2;
  shape: queue;
  font-weight: bold;
  font-size: 13;
}

storage {
  background-color: #1E2A3B;
  color: #93C5FD;
  border-color: #1D4ED8;
  border-width: 2;
  shape: cloud;
  font-weight: bold;
  font-size: 13;
}

/* database[index]: a derived search / secondary index (not the system of
   record). Keeps the database cylinder; an index badge marks the derived role.
   The concrete technology (ElasticSearch, a vector store) belongs in the
   physical layer via the store kind. See Issue #1718. */
database[index] {
  badge-label: "index";
  badge-color: #F59E0B;
}

/* The role axis of a store: no tag = system of record, and the three tags
   below each name a way of *not* being it. [cache] and [analytics] apply to
   storage as well as database (a CDN origin cache, a Parquet data lake), so
   both kinds carry the badge — a kind listed in appliesTo without a selector
   here would be accepted and do nothing. See Issue #2172. */
database[cache], storage[cache] {
  badge-label: "cache";
  badge-color: #22D3EE;
}

database[analytics], storage[analytics] {
  badge-label: "analytics";
  badge-color: #E879F9;
}

/* ── リソースタグ → シェイプ ── */
resource[table]   { shape: cylinder; }
resource[queue]   { shape: queue; }
resource[api]     { shape: hexagon; }
resource[storage] { shape: cloud; }

/* ── デプロイノード種別 ──
   Every deploy kind is one hue, taken three ways: the accent (border-color /
   badge-color) at full chroma, the fill at the lightness end nearest the canvas
   — here the dark end — and the text at the opposite end. The light template
   applies the same rule against a white canvas, so its two ends are swapped.
   war and function used to be desaturated brown and olive that
   belonged to no hue at all, which is what left the accent border floating on
   a muddy card. Hue table: docs/spec/style.md § Kind color vocabulary. */
oci {
  background-color: #16294D;
  color: #93C5FD;
  border-color: #3B82F6;
  badge-label: "oci";
  badge-color: #3B82F6;
}

lambda {
  background-color: #2A1B47;
  color: #D8B4FE;
  border-color: #A855F7;
  badge-label: "lambda";
  badge-color: #A855F7;
}

jar {
  background-color: #12301D;
  color: #86EFAC;
  border-color: #22C55E;
  badge-label: "jar";
  badge-color: #22C55E;
}

war {
  background-color: #3B2210;
  color: #FDBA74;
  border-color: #F97316;
  badge-label: "war";
  badge-color: #F97316;
}

function {
  background-color: #322905;
  color: #FDE047;
  border-color: #EAB308;
  badge-label: "function";
  badge-color: #EAB308;
}

assets {
  background-color: #0B2B33;
  color: #67E8F9;
  border-color: #06B6D4;
  badge-label: "assets";
  badge-color: #06B6D4;
}

job {
  background-color: #3A1414;
  color: #FCA5A5;
  border-color: #EF4444;
  badge-label: "job";
  badge-color: #EF4444;
}

artifact {
  background-color: #25282D;
  color: #D1D5DB;
  border-color: #9CA3AF;
  badge-label: "artifact";
  badge-color: #9CA3AF;
}

store {
  background-color: #0D2E29;
  color: #5EEAD4;
  border-color: #14B8A6;
  badge-label: "store";
  badge-color: #14B8A6;
}

/* ── タグ ── */
[external] {
  background-color: #1F2937;
  border-style: dashed;
}

/* ── アノテーション ── */
/* __ANNOTATION_RULES__ */

/* ── エッジ ── */
edge {
  color: #94A3B8;
  stroke-width: 1.5;
  font-size: 11;
  direction: auto;
}

edge[async] {
  border-style: dashed;
}

edge[read] {
  stroke-width: 1.5;
}

edge[write] {
  stroke-width: 2;
}

edge[cyclic] {
  color: #EF4444;
  stroke-width: 2.5;
}

edge[implicit] {
  color: #F59E0B;
}

/* Warm grey: the base edge's lightness in another hue, so a guessed relation
   reads apart from a confirmed one without looking more or less important (#3115). */
edge[inferred] {
  color: #A8A29E;
}

edge[projected] {
  color: #38BDF8;
}

edge[delivers] {
  color: #A78BFA;
  border-style: dashed;
}
`;

/**
 * karasu built-in theme — light variant (template, see
 * {@link BUILTIN_STYLE_TEMPLATE}).
 *
 * Mirrors the dark template rule-for-rule but with node/edge colors that
 * read well on a light canvas: lighter, less saturated backgrounds, dark
 * text, and slightly darker borders. Badge / accent colors stay saturated
 * so badges remain legible on light cards.
 *
 * Cascade position is identical to the dark sheet (lowest layer); user
 * `.krs.style` still wins. See ADR-1479.
 */
const BUILTIN_STYLE_TEMPLATE_LIGHT: string = `/* karasu built-in default theme (light) */

/* ── ノード種別 ── */
user {
  background-color: #DBEAFE;
  color: #1E3A8A;
  border-color: #93C5FD;
  border-width: 2;
  shape: user;
  font-weight: bold;
  font-size: 13;
}

service {
  background-color: #E0F2FE;
  color: #0C4A6E;
  border-color: #7DD3FC;
  border-width: 2;
  shape: box;
  font-weight: bold;
  font-size: 13;
}

client {
  background-color: #EDE9FE;
  color: #4C1D95;
  border-color: #C4B5FD;
  border-width: 2;
  shape: box;
  font-weight: bold;
  font-size: 13;
}

domain {
  background-color: #F0F9FF;
  color: #0C4A6E;
  border-color: #BAE6FD;
  border-width: 1;
  shape: box;
  font-size: 12;
}

/* Fill-less, as in the dark template — same rule, its own calibration: the
   lightness end that is "far from the canvas" is the dark one here, so this
   border runs the opposite direction from its dark-theme twin. */
usecase {
  background-color: transparent;
  color: #155E86;
  border-color: #1D3646;
  border-width: 1;
  shape: box;
  font-size: 12;
}

entity {
  background-color: #F5F3FF;
  color: #4C1D95;
  border-color: #C4B5FD;
  border-width: 1;
  shape: box;
  font-size: 12;
}

resource {
  background-color: #EEF1F5;
  color: #334155;
  border-color: #94A3B8;
  border-width: 2;
  font-size: 12;
}

team {
  background-color: #D1FAE5;
  color: #064E3B;
  border-color: #6EE7B7;
  border-width: 2;
  shape: box;
  font-weight: bold;
  font-size: 13;
}

member {
  background-color: #F0F9FF;
  color: #0C4A6E;
  border-color: #BAE6FD;
  border-width: 1;
  shape: user;
  font-size: 12;
}

/* ── インフラリソース種別 ── */
database {
  background-color: #DCFCE7;
  color: #14532D;
  border-color: #86EFAC;
  border-width: 2;
  shape: cylinder;
  font-weight: bold;
  font-size: 13;
}

queue {
  background-color: #FEF3C7;
  color: #78350F;
  border-color: #FCD34D;
  border-width: 2;
  shape: queue;
  font-weight: bold;
  font-size: 13;
}

storage {
  background-color: #DBEAFE;
  color: #1E3A8A;
  border-color: #93C5FD;
  border-width: 2;
  shape: cloud;
  font-weight: bold;
  font-size: 13;
}

/* database[index]: derived search / secondary index. See Issue #1718. */
database[index] {
  badge-label: "index";
  badge-color: #B45309;
}

/* The store role axis — [cache] / [analytics] name two ways of not being the
   system of record, on both database and storage. See Issue #2172. */
database[cache], storage[cache] {
  badge-label: "cache";
  badge-color: #0E7490;
}

database[analytics], storage[analytics] {
  badge-label: "analytics";
  badge-color: #A21CAF;
}

/* ── リソースタグ → シェイプ ── */
resource[table]   { shape: cylinder; }
resource[queue]   { shape: queue; }
resource[api]     { shape: hexagon; }
resource[storage] { shape: cloud; }

/* ── デプロイノード種別 ── */
oci {
  background-color: #DBEAFE;
  color: #1E3A8A;
  border-color: #3B82F6;
  badge-label: "oci";
  badge-color: #1D4ED8;
}

lambda {
  background-color: #F3E8FF;
  color: #4C1D95;
  border-color: #A855F7;
  badge-label: "lambda";
  badge-color: #7E22CE;
}

jar {
  background-color: #DCFCE7;
  color: #14532D;
  border-color: #22C55E;
  badge-label: "jar";
  badge-color: #15803D;
}

war {
  background-color: #FFEDD5;
  color: #7C2D12;
  border-color: #F97316;
  badge-label: "war";
  badge-color: #C2410C;
}

function {
  background-color: #FEF9C3;
  color: #713F12;
  border-color: #EAB308;
  badge-label: "function";
  badge-color: #A16207;
}

assets {
  background-color: #CFFAFE;
  color: #155E75;
  border-color: #06B6D4;
  badge-label: "assets";
  badge-color: #0E7490;
}

job {
  background-color: #FEE2E2;
  color: #7F1D1D;
  border-color: #EF4444;
  badge-label: "job";
  badge-color: #B91C1C;
}

artifact {
  background-color: #F3F4F6;
  color: #1F2937;
  border-color: #9CA3AF;
  badge-label: "artifact";
  badge-color: #4B5563;
}

store {
  background-color: #CCFBF1;
  color: #115E59;
  border-color: #0D9488;
  badge-label: "store";
  badge-color: #0F766E;
}

/* ── タグ ── */
[external] {
  background-color: #F3F4F6;
  border-style: dashed;
}

/* ── アノテーション ── */
/* __ANNOTATION_RULES__ */

/* ── エッジ ── */
edge {
  color: #64748B;
  stroke-width: 1.5;
  font-size: 11;
  direction: auto;
}

edge[async] {
  border-style: dashed;
}

edge[read] {
  stroke-width: 1.5;
}

edge[write] {
  stroke-width: 2;
}

edge[cyclic] {
  color: #DC2626;
  stroke-width: 2.5;
}

edge[implicit] {
  color: #B45309;
}

/* Warm grey: see the dark sheet (#3115). */
edge[inferred] {
  color: #78716C;
}

edge[projected] {
  color: #0369A1;
}

edge[delivers] {
  color: #7C3AED;
  border-style: dashed;
}
`;

/** Assemble the full builtin sheet source for a theme + injected labels. */
export function buildBuiltinStyleSource(
  theme: DiagramTheme = "dark",
  badgeLabels?: AnnotationBadgeLabels,
): string {
  const template = theme === "light" ? BUILTIN_STYLE_TEMPLATE_LIGHT : BUILTIN_STYLE_TEMPLATE;
  return template.replace(ANNOTATION_RULES_PLACEHOLDER, buildAnnotationRules(theme, badgeLabels));
}

/**
 * Full builtin sheet text with the default (reference-data en) badge
 * labels. Kept as exported constants for the Reference panel
 * (`reference.ts`) and tests that inspect the sheet text.
 */
export const BUILTIN_STYLE_SOURCE: string = buildBuiltinStyleSource("dark");
export const BUILTIN_STYLE_SOURCE_LIGHT: string = buildBuiltinStyleSource("light");

// Parsed-sheet cache keyed by (theme, badge label set). The label space is
// the caller's locale table, so the cache stays tiny (themes × locales).
const _sheetCache = new Map<string, StyleSheet>();

function sheetCacheKey(theme: DiagramTheme, badgeLabels?: AnnotationBadgeLabels): string {
  // Derived from `REFERENCE_DATA.annotations` rather than listed by hand: a
  // hand-written list silently stops covering a newly added annotation, and
  // the symptom is a *wrong label served from cache*, which no test of the
  // sheet's content would notice. `@draft` shipped that way (#1995).
  //
  // JSON.stringify gives an unambiguous key — a join() separator could
  // collide with a separator-containing label from an external caller.
  return JSON.stringify([
    theme,
    ...REFERENCE_DATA.annotations.map((a) => badgeLabels?.[ANNOTATION_LABEL_KEYS[a.name]] ?? ""),
  ]);
}

/**
 * Return the built-in `.krs.style` sheet for the given theme and optional
 * injected annotation badge labels. `"dark"` + default labels is the
 * legacy fast path; existing callers that pass nothing are unchanged.
 */
export function getBuiltinStyleSheet(
  theme: DiagramTheme = "dark",
  badgeLabels?: AnnotationBadgeLabels,
): StyleSheet {
  const key = sheetCacheKey(theme, badgeLabels);
  let sheet = _sheetCache.get(key);
  if (!sheet) {
    sheet = parseBuiltinSheet(buildBuiltinStyleSource(theme, badgeLabels));
    _sheetCache.set(key, sheet);
  }
  return sheet;
}

/**
 * `StyleRule.sheetId` for the built-in sheet. Exported because consumers have to
 * tell karasu's own defaults apart from what an author wrote — `resolveTeamFrames`
 * does, so the card's defaults do not follow a selector onto the frame (#2269).
 */
export const BUILTIN_SHEET_ID = "<builtin>";

function parseBuiltinSheet(source: string): StyleSheet {
  const result = StyleParser.parse(source, BUILTIN_SHEET_ID);
  /* c8 ignore next 4 */
  if (result.diagnostics.length > 0) {
    throw new Error(
      `Built-in stylesheet has parse errors: ${result.diagnostics.map((d) => d.code).join(", ")}`,
    );
  }
  return result.value;
}
